import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { ApiResponse, ApiServiceInterface } from '@hutsix/ngxh6';

interface Quarter {
    value: string;
    label: string;
    current: boolean;
}

@Component({
    selector: 'app-quarter-selector',
    templateUrl: './quarter-selector.component.html',
    styleUrls: ['./quarter-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class QuarterSelectorComponent implements OnInit {
    @Output() quarterChange = new EventEmitter<string | null>();

    quarters: Quarter[] = [];
    selected: string | null = null;

    /**
     * Australian financial quarter — FY runs Jul→Jun, labelled by FY-end year.
     *   month 7-9  → Q1 (FY = year + 1)
     *   month 10-12→ Q2 (FY = year + 1)
     *   month 1-3  → Q3 (FY = year)
     *   month 4-6  → Q4 (FY = year)
     */
    static getCurrentQuarter(): string {
        const now = new Date();
        const month = now.getMonth() + 1;
        const year = now.getFullYear();
        const fy = month >= 7 ? year + 1 : year;
        let q: number;
        if (month >= 7 && month <= 9) q = 1;
        else if (month >= 10 && month <= 12) q = 2;
        else if (month >= 1 && month <= 3) q = 3;
        else q = 4;
        return `${fy}-Q${q}`;
    }

    /**
     * Human label for a quarter value, matching the backend's QuarterHelper:
     *   "2026-Q4" → "Q4 FY26 (Apr–Jun)"
     */
    static labelFor(value: string): string {
        const m = value?.match(/^(\d{4})-Q([1-4])$/);
        if (!m) return value || '';
        const q = Number(m[2]);
        const months = ['', 'Jul–Sep', 'Oct–Dec', 'Jan–Mar', 'Apr–Jun'][q];
        return `Q${q} FY${m[1].slice(2)} (${months})`;
    }

    constructor(
        @Inject('ApiService') private api: ApiServiceInterface,
        private cdRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.api.get({ url: '/api/quarters', useCache: false }).subscribe(
            (res: ApiResponse<any>) => {
                this.quarters = res.data?.quarters || [];
                this.selected = res.data?.current || '';
                this.cdRef.markForCheck();
                this.quarterChange.emit(this.selected || null);
            },
            () => {
                // ApiService rejects with an ApiErrorResponse, which is not an Error. Without
                // this handler an offline blip or a 5xx on /api/quarters escapes to the global
                // handler and Bugsnag records it as "received a non-error" against whatever
                // page the selector was on. ApiService already raises its own toast, and every
                // host page seeds itself with getCurrentQuarter(), so hiding the selector (empty
                // quarters) and emitting nothing leaves the page working as it does today.
                this.quarters = [];
                this.selected = null;
                this.cdRef.markForCheck();
            },
        );
    }

    onChange(value: string): void {
        this.selected = value;
        this.quarterChange.emit(value || null);
    }
}
